/**
 * useBroadcast — рассылка заданиями (06.10.2026; владелец: «вчера рассылка с Юнифанса не
 * получилась»). 05.10 отправка шла одним запросом две минуты без прогресса, итог мелькнул на
 * 2,5 с, вторая попытка оборвалась и прислала повторы. Теперь: черновики → предпросмотр отбора
 * (кому уйдёт и кого отсеем) → задание на сервере → ход раз в 2 с, пока экран виден, и
 * «подтолкнуть» отправку, пока панель открыта. Закрыть панель или приложение можно — досылает
 * сервер (минутный крон). При повторном открытии идущая рассылка подхватывается.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../api/client";
import { ApiException } from "../../../api/types";

export interface Draft { id: number; kind: string; preview: string; created_at: string; author?: string | null; sent?: number }
export interface Buckets { requested: number; eligible: number; staff: number; excluded: number; already: number; unreachable: number; optout?: number }
export interface BroadcastJob {
  id: number; draft_id: number; kind: string; status: "running" | "done" | "cancelled" | "failed" | string;
  method: string; preview: string; total: number; skipped: number; created_by_name: string | null;
  created_at: string; finished_at: string | null; error: string; leased: boolean; next_run_at: number;
  counts: Record<string, number>; errors: { error: string; n: number }[];
}

const POLL_MS = 2000;
const PUMP_EVERY_MS = 5000;
const errText = (e: unknown, fb: string) => (e instanceof ApiException ? e.body?.error || `Ошибка ${e.status}` : e instanceof Error && e.message ? `${fb}: ${e.message}` : fb);
const newKey = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random()}`);

export function useBroadcast(selectedIds: number[]) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [bot, setBot] = useState("");
  const [loading, setLoading] = useState(true);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [preview, setPreview] = useState<Buckets | null>(null);
  const [job, setJob] = useState<BroadcastJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pv, setPv] = useState(0);
  /** Идущая рассылка коллеги (или своя, открытая с новым выделением) — плашка «Посмотреть», а не захват формы. */
  const [other, setOther] = useState<BroadcastJob | null>(null);
  /** Досылка после «исходник удалён»: прежнее задание — получившие его не получат повтор. */
  const [continueOf, setContinueOf] = useState<BroadcastJob | null>(null);
  const inFlight = useRef(false);
  const keyRef = useRef(newKey());
  const lastPump = useRef(0);
  const idsKey = selectedIds.join(",");

  const loadDrafts = useCallback(() => {
    setLoading(true);
    api<{ drafts: Draft[]; bot?: string }>("/api/broadcast/drafts")
      .then((r) => { const list = r.drafts ?? []; setDrafts(list); setBot(r.bot ?? ""); setDraftId((p) => p ?? list[0]?.id ?? null); })
      .catch((e: unknown) => setError(errText(e, "Не удалось загрузить черновики")))
      .finally(() => setLoading(false));
  }, []);

  /* открыли панель: черновики + идущая рассылка (её показываем сразу, а не форму) */
  useEffect(() => {
    loadDrafts();
    api<{ jobs: BroadcastJob[] }>("/api/broadcast/jobs")
      .then((r) => {
        const run = (r.jobs ?? []).find((j) => j.status === "running" && j.kind === "normal");
        /* открыли без выделения — следим за идущей; с выделением — форма, идущая плашкой */
        if (run) { if (selectedIds.length) setOther(run); else setJob(run); }
      })
      .catch(() => {});
  }, [loadDrafts]); // eslint-disable-line react-hooks/exhaustive-deps

  /* предпросмотр отбора по черновику и выделению; новый набор — новый ключ повтора */
  useEffect(() => {
    keyRef.current = newKey();
    setError(null);
    if (!draftId || !selectedIds.length) { setPreview(null); return; }
    let dead = false;
    const t = window.setTimeout(() => {
      api<Buckets>("/api/broadcast/preview", { method: "POST", body: { draftId, telegramIds: selectedIds, continueOf: continueOf?.id }, retries: 2 })
        .then((b) => { if (!dead) setPreview(b); })
        .catch(() => { if (!dead) setPreview(null); });
    }, 250);
    return () => { dead = true; window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, idsKey, pv, continueOf?.id]);

  /* ход задания: опрос, пока экран виден; пока панель открыта — подталкиваем отправку */
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const id = job.id;
    const tick = async () => {
      if (document.visibilityState !== "visible" || inFlight.current) return;
      inFlight.current = true;
      try {
        const r = await api<{ job: BroadcastJob }>(`/api/broadcast/jobs/${id}`, { retries: 1 });
        /* запоздалый ответ «идёт» не возвращает уже остановленную или законченную рассылку */
        setJob((prev) => (prev && prev.id === id && prev.status !== "running" && r.job.status === "running" ? prev : r.job));
        const now = Date.now();
        if (r.job.status === "running" && !r.job.leased && r.job.next_run_at <= now && now - lastPump.current > PUMP_EVERY_MS) {
          lastPump.current = now;
          await api(`/api/broadcast/jobs/${id}/pump`, { method: "POST" }).catch(() => {});
        }
      } catch { /* связь — следующий тик */ } finally { inFlight.current = false; }
    };
    const t = window.setInterval(() => { void tick(); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Запуск: kind 'test' — только себе. Ключ повтора защищает от двойного нажатия и повтора сети. */
  const start = async (kind: "normal" | "test"): Promise<BroadcastJob | null> => {
    if (!draftId) return null;
    setBusy(true);
    setError(null);
    try {
      const idemKey = kind === "test" ? newKey() : keyRef.current;
      const r = await api<{ job: BroadcastJob }>("/api/broadcast/jobs", { method: "POST", body: { draftId, telegramIds: selectedIds, idemKey, kind, continueOf: kind === "normal" ? continueOf?.id : undefined }, retries: 2 });
      if (kind === "normal") { setJob(r.job); setContinueOf(null); setOther(null); keyRef.current = newKey(); }
      return r.job;
    } catch (e) {
      setError(errText(e, "Рассылка не запустилась"));
      if (e instanceof ApiException && e.status === 409) {
        const jid = Number((e.body as { job_id?: number } | null)?.job_id);
        if (jid) api<{ job: BroadcastJob }>(`/api/broadcast/jobs/${jid}`).then((r) => setJob(r.job)).catch(() => {});
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!job) return;
    setBusy(true);
    try {
      const r = await api<{ job: BroadcastJob }>(`/api/broadcast/jobs/${job.id}/cancel`, { method: "POST" });
      setJob(r.job);
    } catch (e) {
      setError(errText(e, "Не удалось остановить"));
    } finally {
      setBusy(false);
    }
  };

  /** «Новая рассылка»: к форме, черновики и отбор — заново (после рассылки «уже получали» вырос). */
  const reset = (cont: BroadcastJob | null = null) => { setJob(null); setError(null); setPreview(null); setContinueOf(cont); loadDrafts(); setPv((n) => n + 1); };
  const watch = (j: BroadcastJob) => { setOther(null); setJob(j); };

  /** «Тест себе»: ждём исход (до ~12 с) — сообщаем, дошло ли, а не «отправлено» сразу. */
  const testOutcome = async (j: BroadcastJob): Promise<BroadcastJob> => {
    let cur = j;
    for (let i = 0; i < 8 && cur.status === "running"; i++) {
      await new Promise((r) => window.setTimeout(r, 1500));
      cur = (await api<{ job: BroadcastJob }>(`/api/broadcast/jobs/${j.id}`).catch(() => ({ job: cur }))).job;
      if (cur.status === "running" && !cur.leased) await api(`/api/broadcast/jobs/${j.id}/pump`, { method: "POST" }).catch(() => {});
    }
    return cur;
  };

  return { drafts, bot, loading, draftId, setDraftId, preview, job, busy, error, start, cancel, loadDrafts, reset, other, watch, continueOf, testOutcome };
}
